import type { ShortVideo } from '~/composables/shorts';
import type { Property, ShortVideoProperty } from '~/core/entities/property';
import type {
	CollectionPage,
	Graph,
	ItemList,
	ListItem,
	Thing,
	VideoObject,
	WebPage
} from 'schema-dts';

import { PropertyPublicationStatus } from '~/core/entities/api/ApiProperty';

export type ShortsJsonLdGraph = Graph;

interface BuildShortsVideoJsonLdParams {
	property: Property | null | undefined;
	websiteUrl: string;
}

interface BuildShortsHomeFeedJsonLdParams {
	pageUrl: string;
	name: string;
	description: string;
	videos: readonly ShortVideo[];
}

function trimmed(value: string | null | undefined): string {
	return value?.trim() ?? '';
}

function toAbsoluteHttpUrl(
	value: string | null | undefined,
	baseUrl: string
): string | null {
	const rawValue = trimmed(value);
	if (!rawValue) return null;

	try {
		const url = new URL(rawValue, baseUrl);
		if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
		return url.toString();
	} catch {
		return null;
	}
}

function toHttpUrl(value: string | null | undefined): string | null {
	const rawValue = trimmed(value);
	if (!rawValue) return null;

	try {
		const url = new URL(rawValue);
		if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
		return url.toString();
	} catch {
		return null;
	}
}

function toIsoDateTime(value: string | null | undefined): string | null {
	const rawValue = trimmed(value);
	if (!rawValue) return null;

	const date = new Date(rawValue);
	if (Number.isNaN(date.getTime())) return null;

	return date.toISOString();
}

export function secondsToIsoDuration(
	durationSeconds: number | null | undefined
): string | undefined {
	if (!durationSeconds || durationSeconds <= 0) return undefined;

	const totalSeconds = Math.floor(durationSeconds);
	if (totalSeconds <= 0) return undefined;

	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	const parts = [
		hours ? `${hours}H` : '',
		minutes ? `${minutes}M` : '',
		seconds ? `${seconds}S` : ''
	].join('');

	return `PT${parts || '0S'}`;
}

function buildShortsPath(commercialId: string): string {
	return `/shorts/${commercialId}`;
}

function getPropertyTitle(property: Property | ShortVideoProperty): string {
	return trimmed(property.ad?.title);
}

function getPropertyTypeName(property: Property | ShortVideoProperty): string {
	return trimmed(property.propertyType?.name) || 'Imovel';
}

function buildFallbackDescription(
	property: Property | ShortVideoProperty
): string {
	const propertyType = getPropertyTypeName(property);
	const location = [property.region, property.city].filter(Boolean).join(', ');
	const area = property.area > 0 ? `${property.area} m2` : '';
	const bedrooms = property.bedrooms ? `${property.bedrooms} quartos` : '';
	const suites = property.suites ? `${property.suites} suites` : '';
	const details = [area, bedrooms, suites].filter(Boolean).join(', ');
	const locationPart = location ? ` em ${location}` : '';
	const detailsPart = details ? ` com ${details}` : '';

	return `${propertyType}${detailsPart}${locationPart} no PilarHomes Shorts.`;
}

function getPropertyDescription(property: Property): string {
	return trimmed(property.ad.description) || buildFallbackDescription(property);
}

export class ShortsJsonLdPresenter {
	static getShortsVideoJsonLd({
		property,
		websiteUrl
	}: BuildShortsVideoJsonLdParams): ShortsJsonLdGraph | null {
		if (!property) return null;
		if (property.publicationStatus !== PropertyPublicationStatus.PUBLISHED) {
			return null;
		}

		const commercialId = trimmed(property.commercialId);
		const title = getPropertyTitle(property);
		const video = property.featuredVideos[0];
		if (!commercialId || !title || !video) return null;

		const watchPageUrl = toAbsoluteHttpUrl(
			buildShortsPath(commercialId),
			websiteUrl
		);
		const thumbnailUrl = toAbsoluteHttpUrl(
			video.thumbnail || property.thumbnailUrl,
			websiteUrl
		);
		const contentUrl = toAbsoluteHttpUrl(video.url, websiteUrl);
		const uploadDate = toIsoDateTime(video.processedAt);

		if (!watchPageUrl || !thumbnailUrl || !contentUrl || !uploadDate) {
			return null;
		}

		const webpageId = `${watchPageUrl}#webpage`;
		const videoId = `${watchPageUrl}#video`;
		const description = getPropertyDescription(property);
		const duration = secondsToIsoDuration(video.duration);

		const webpage = {
			'@type': 'WebPage',
			'@id': webpageId,
			url: watchPageUrl,
			name: `${title} | Shorts | PilarHomes`,
			description,
			mainEntity: { '@id': videoId }
		} satisfies WebPage;

		const videoObject = {
			'@type': 'VideoObject',
			'@id': videoId,
			name: title,
			description,
			thumbnailUrl: [thumbnailUrl],
			uploadDate,
			...(duration ? { duration } : {}),
			contentUrl,
			url: watchPageUrl,
			mainEntityOfPage: { '@id': webpageId }
		} satisfies VideoObject;

		const graph = [webpage, videoObject] satisfies Thing[];

		return {
			'@context': 'https://schema.org',
			'@graph': graph
		};
	}

	static getShortsHomeFeedJsonLd({
		pageUrl,
		name,
		description,
		videos
	}: BuildShortsHomeFeedJsonLdParams): ShortsJsonLdGraph | null {
		const canonicalPageUrl = toHttpUrl(pageUrl);
		const pageName = trimmed(name);
		const pageDescription = trimmed(description);
		if (!canonicalPageUrl || !pageName || !pageDescription) return null;

		const seenCommercialIds = new Set<string>();
		const itemListElement = videos
			.filter(item => item.videoUrl)
			.map(item => trimmed(item.property.commercialId))
			.filter(commercialId => {
				if (!commercialId || seenCommercialIds.has(commercialId)) {
					return false;
				}

				seenCommercialIds.add(commercialId);
				return true;
			})
			.map(
				(commercialId, index) =>
					({
						'@type': 'ListItem',
						position: index + 1,
						url: new URL(
							buildShortsPath(commercialId),
							canonicalPageUrl
						).toString()
					}) satisfies ListItem
			);

		if (!itemListElement.length) return null;

		const webpageId = `${canonicalPageUrl}#webpage`;
		const itemListId = `${canonicalPageUrl}#itemlist`;

		const collectionPage = {
			'@type': 'CollectionPage',
			'@id': webpageId,
			url: canonicalPageUrl,
			name: pageName,
			description: pageDescription,
			mainEntity: { '@id': itemListId }
		} satisfies CollectionPage;

		const itemList = {
			'@type': 'ItemList',
			'@id': itemListId,
			name: pageName,
			itemListElement
		} satisfies ItemList;

		const graph = [collectionPage, itemList] satisfies Thing[];

		return {
			'@context': 'https://schema.org',
			'@graph': graph
		};
	}
}
